import styled from 'styled-components';

import { Card } from '@components/card';
import { HtmlContent } from '@components/html-content';
import { Modal, useModal } from '@components/modal';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import { useEmailDomains, useEmailSettings } from 'api/company/hooks';
import { useActionEmailTemplate } from 'api/journeys/hooks';

import { AttachmentList } from '../common';
export const usePreviewEmailModal = () => useModal(PreviewEmail, { variant: 'LARGE' });

const EMPTY_EMAIL = '<p></p>';

export type PreviewEmailProps = {
  action: 'journey' | 'traveller';
  shortId: string;
  to: string | string[];
  cc?: string | string[];
  bcc?: string | string[];
  replyTo?: string | string[];
  content?: string;
  subject?: string;
  attachments?: string[];
  sendFromEmailId?: string;
};

type EmailItemProps = {
  infoText: string;
  text: string;
};

export const PreviewEmail = (props: PreviewEmailProps) => {
  const { to, subject, shortId, action, attachments, cc, bcc, replyTo, sendFromEmailId } = props;
  const { t } = useScopedTranslate(locale);
  const { data } = useActionEmailTemplate(action, shortId);

  const formatRecipientList = (
    value?: string | string[],
    emptyFallback: string | null = null
  ): string | null => {
    return Array.isArray(value) && value.length > 0 ? value.join(', ') : emptyFallback;
  };

  const recipient = formatRecipientList(to, t('noRecipient')) as string;
  const ccInfo = formatRecipientList(cc);
  const bccInfo = formatRecipientList(bcc);
  const replyToInfo = formatRecipientList(replyTo);
  const subjectInfo = subject ? subject : t('noSubject');
  const emailSettingsQuery = useEmailSettings();
  const emailSettings = emailSettingsQuery.data;
  const domainsQuery = useEmailDomains();
  const emailAttachments = attachments ?? [];

  const resolveFromAddress = (): { name: string; email: string } => {
    if (!sendFromEmailId || sendFromEmailId === 'system') {
      return { name: '50skills', email: 'noreply@mg.50skills.app' };
    }

    if (domainsQuery.data) {
      for (const domain of domainsQuery.data) {
        const address = domain.emailAddresses.find((addr) => addr.shortId === sendFromEmailId);
        if (address) {
          return { name: address.fromName, email: address.fromAddress };
        }
      }
    }

    return {
      name: emailSettings?.fromName ?? '50skills',
      email: emailSettings?.fromAddress ?? 'noreply@mg.50skills.app',
    };
  };

  const fromAddress = resolveFromAddress();

  return (
    <Modal>
      <Modal.SimpleHeader title={t('emailPreview')} />
      <ContentContainer>
        <Modal.Content>
          <Stack space={8}>
            <Stack space={2}>
              <Stack space={1}>
                <EmailItem
                  infoText={t('from')}
                  text={t('email', {
                    name: fromAddress.name,
                    email: fromAddress.email,
                  })}
                />
                <Separator />
              </Stack>
              <Stack space={1}>
                <EmailItem infoText={t('to')} text={recipient} />
                <Separator />
              </Stack>
              {ccInfo && (
                <Stack space={1}>
                  <EmailItem infoText={t('cc')} text={ccInfo} />
                  <Separator />
                </Stack>
              )}
              {bccInfo && (
                <Stack space={1}>
                  <EmailItem infoText={t('bcc')} text={bccInfo} />
                  <Separator />
                </Stack>
              )}
              {replyToInfo && (
                <Stack space={1}>
                  <EmailItem infoText={t('replyTo')} text={replyToInfo} />
                  <Separator />
                </Stack>
              )}
              <Stack space={1}>
                <EmailItem infoText={t('subject')} text={subjectInfo} />
                {emailAttachments.length > 0 && <Separator />}
              </Stack>

              {emailAttachments.length > 0 && (
                <Stack space={4} align="top" orientation="horizontal">
                  <StyledText color="textSecondary" font="semibold14" text={t('attachments')} />
                  <Stack space={1}>
                    <AttachmentList attachments={emailAttachments} />
                  </Stack>
                </Stack>
              )}
            </Stack>
            <Card space={1}>
              <HtmlContent font="regular14" html={data || EMPTY_EMAIL} />
            </Card>
          </Stack>
        </Modal.Content>
      </ContentContainer>
    </Modal>
  );
};

const EmailItem = (props: EmailItemProps) => {
  const { text, infoText } = props;
  return (
    <Stack space={4} align="center" orientation="horizontal">
      <StyledText color="textSecondary" font="semibold14" text={infoText} />
      <Text font="regular14" text={text} />
    </Stack>
  );
};

const ContentContainer = styled.div({
  overflowY: 'auto',
  marginTop: pxToRem(24),
});

const StyledText = styled(Text)({
  width: pxToRem(120),
});

const locale = {
  en: {
    emailPreview: 'Email preview',
    from: 'FROM',
    to: 'TO',
    cc: 'CC',
    bcc: 'BCC',
    replyTo: 'REPLY-TO',
    subject: 'SUBJECT',
    poweredBy: 'Powered by 50skills',
    email: '{name} <{email}>',
    noContent: 'Email has no content',
    noSubject: 'Email has no subject',
    noRecipient: 'Email has no recipients',
    attachments: 'ATTACHMENTS',
  },
};
